import _ from 'lodash'
import {
  getOrderUniqueImages
} from '../../Proposal/Common/ProposalForm/Shared/modelsUtils'
import {
  DEFAULT_DISTINCT_DIMENSIONS
} from '../../Proposal/Common/ImageUploader/DistinctSizesUploader/DistinctSizesUploader'
import {
  UPDATE_IMAGE_V2,
  getPostFormatImages
} from '../../Proposal/UpdateListing/helpers/converters'
import { FIELD as scheduledField, BATCH_PROPOSAL_TYPE } from '../Common/ScheduledBlock'
import { getISO8601UTC } from '../../../lib/MomentHelper'
import { PROPOSAL_REVIEW_STATUS } from '@vzmi/shp_shared_compo/dist/constants/proposal.js'

export const getListingWithImagesModels = listing => {
  let models = _.get(listing, 'models', [])
  const images = _.get(listing, 'images', [])
  const isNoModelsListing = listing.isNoModelsListing || _.isEmpty(models)
  if (isNoModelsListing) {
    models = [{
      sku: listing.sku,
      images
    }]
  }
  models = models.map((model, index) => {
    const _hasSameSku = o => o.sku === _.get(model, 'sku')
    const modelImages = _.get(model, 'images', [])
    const orderUniqueImages = getOrderUniqueImages(modelImages)
    const withoutFirstOrderImagesMap = _.flow(
      () => _.filter(modelImages, img => img.width !== 1000),
      filteredImgs => _.groupBy(filteredImgs, 'order'),
      imagesGroupByOrder => {
        const defaultDimensionsMap = _.reduce(imagesGroupByOrder, (map, currentImgs, order) => {
          let orderImgs = []
          if (_.size(currentImgs) === _.size(DEFAULT_DISTINCT_DIMENSIONS)) {
            orderImgs = currentImgs
          } else {
            const imgsMapByWidth = _.groupBy(currentImgs, 'width')
            const defaultDimensionsImgs = _.map(DEFAULT_DISTINCT_DIMENSIONS, dimension => {
              const dimensionImg = _.get(imgsMapByWidth, `${dimension}[0]`)
              if (_.isEmpty(dimensionImg)) {
                return { order, height: dimension, width: dimension }
              }
              return dimensionImg
            })
            orderImgs = defaultDimensionsImgs
          }
          orderImgs = _.orderBy(orderImgs, ['width'], 'desc')
          map[order] = orderImgs
          return map
        }, {})
        return defaultDimensionsMap
      }
    )()
    const modelFirstOrderImages = _.reduce(orderUniqueImages, (orderImgMap, image) => {
      const { url, order } = image
      if (order !== 1) {
        return orderImgMap
      }
      const withoutFirstOrderImages = _.get(withoutFirstOrderImagesMap, order, [])
      orderImgMap[url] = {
        defaultImages: withoutFirstOrderImages,
        currentImages: _.cloneDeep(withoutFirstOrderImages)
      }
      return orderImgMap
    }, {})
    const firstIdxInList = _.findIndex(models, _hasSameSku)
    const lastIdxInList = _.findLastIndex(models, _hasSameSku)
    const duplicated = firstIdxInList !== lastIdxInList
    const editable = !duplicated || firstIdxInList === index
    return {
      ...model,
      firstOrderImages: modelFirstOrderImages,
      images: orderUniqueImages,
      editable
    }
  })
  const syncProductImages = isNoModelsListing
    // 無屬性賣場檢視頁會從提案判斷是否同時修改商品圖
    // 預設修改賣場圖片提案時同時修改商品圖固定為 true
    ? _.get(listing, 'proposal.listing.syncProductImages', true)
    : true
  return { ...listing, models, isNoModelsListing, syncProductImages }
}

export const getConvertedImageListingParams = (listing, isSubProposal = false) => {
  const {
    models = [],
    isNoModelsListing,
    shareMediaBetweenModels
  } = listing
  let convertedImagesParams
  if (isNoModelsListing) {
    // 無屬性賣場修改圖片帶 images
    convertedImagesParams = {
      images: getPostFormatImages(_.get(models, '0'))
    }
  } else {
    // 共用商品圖只用第一個 model
    const convertedModels = shareMediaBetweenModels
      // 共用商品圖時只有第一筆 model 會帶圖片
      ? [
        _.first(models),
        ...(
          // 修改圖片子提案時剩下商品會濾掉圖片但是允許之後帶入 partNo 等欄位
          isSubProposal
            ? _.flow(
              () => _.slice(models, 1),
              slicedModels => _.map(slicedModels, model => _.omit(model, ['images']))
            )()
            : []
        )
      ]
      : models
    // 單雙層屬性賣場修改圖片帶 models
    convertedImagesParams = {
      models: _.map(convertedModels, model => {
        const images = _.get(model, 'images')
        return {
          ..._.pick(model, ['sku', 'spec']),
          ...(!_.isUndefined(images) && ({
            images: getPostFormatImages(model)
          }))
        }
      })
    }
  }
  return convertedImagesParams
}

export const getParamsList = (formValues, reviewStatus, duplicatedMap) => {
  const {
    applicant,
    listings = []
  } = formValues
  // 共用商品賣場不能修改所以預先剔除
  const filteredListings = _.filter(listings, listing => !(listing.id in duplicatedMap))
  const updateParamsList = _.map(filteredListings, listing => {
    const {
      isNoModelsListing,
      proposal: {
        id: proposalId
      } = {}
    } = listing
    const convertedImagesParams = getConvertedImageListingParams(listing)
    const convertedListing = {
      ..._.pick(listing, [
        ...['id', 'shareMediaBetweenModels', 'swCode'],
        // 無屬性賣場可以修改是否要同時修改商品圖
        ...(isNoModelsListing ? ['syncProductImages'] : [])
      ]),
      ...convertedImagesParams
    }
    const updateParams = {
      reviewStatus,
      applicant,
      id: proposalId,
      type: UPDATE_IMAGE_V2,
      listing: convertedListing
    }
    return updateParams
  })
  return updateParamsList
}

// 取得預約回檔批次請求的 params
export const getBatchProposalParams = (scheduled = {}) => {
  const {
    [scheduledField.IS_SCHEDULED]: isScheduled,
    [scheduledField.IS_REVERT]: isRevert,
    [scheduledField.EXECUTE_TS]: executeTs,
    [scheduledField.REVERT_TS]: revertTs
  } = scheduled
  const instantExecute = !isScheduled && isRevert
  return {
    proposalType: 'updateImageV2',
    batchSource: 'page',
    // 批次修改如果沒有預約則需要立即執行
    instantExecute,
    ...(isScheduled && { executeTs: getISO8601UTC(executeTs) }),
    ...(isRevert && { revertTs: getISO8601UTC(revertTs) })
  }
}

// 取得更新賣場要用到的批次欄位
export const getUpdateListingBatchFields = (createBatchPayload = {}) => {
  const { id, executeTs, revertTs, instantExecute } = createBatchPayload
  const isExecuteTsExist = !_.isEmpty(executeTs)
  const isRevertTsExist = !_.isEmpty(revertTs)
  const batches = []
  // 立即執行
  if (instantExecute) {
    batches.push({ id, type: BATCH_PROPOSAL_TYPE.EXECUTE, isInstantExecute: true })
  }

  // 預約
  if (isExecuteTsExist && !instantExecute) {
    batches.push({ id, type: BATCH_PROPOSAL_TYPE.EXECUTE, isInstantExecute: false })
  }

  // 回檔
  if (isRevertTsExist) {
    batches.push({ id, type: BATCH_PROPOSAL_TYPE.REVERT, isInstantExecute: false })
  }
  return batches
}

// 取得送出提案的 params，如果同時有批次與預約 params 數量會變成兩倍，預約與回檔需要分開發請求
export const getParamsListWithBatch = (getParamsListByIsRevert, batches) => {
  const paramsListWithBatch = _.flow(
    () => _.map(batches, ({ isInstantExecute, ...batch }) => {
      const batchType = _.get(batch, 'type')
      const isRevert = batchType === BATCH_PROPOSAL_TYPE.REVERT
      const paramsList = getParamsListByIsRevert(isRevert)
      return _.map(paramsList, params => ({
        // (預約/立即執行)與回檔需要分開發請求，回檔直接建立新提案所以拿掉 id
        ..._.omit(params, isRevert ? ['id'] : []),
        reviewStatus: isInstantExecute
          // 沒有預約或回檔時，代表是立即執行 + 回檔，所以 reviewStatus 要用 pendingReview
          ? PROPOSAL_REVIEW_STATUS.PENDING_REVIEW
          // 預約與回檔不直接讓修改被執行，所以 reviewStatus 維持 draft
          : PROPOSAL_REVIEW_STATUS.DRAFT,
        // 帶入(預約/立即執行)、回檔的欄位
        batch
      }))
    }),
    paramsListWithBatchList => _.concat(...paramsListWithBatchList)
  )()
  return paramsListWithBatch
}

export const getBatchDetailParams = (batches = [], proposalResults = []) => {
  const batchId = _.get(batches, '0.id')
  // 按照 listingId 組成預約與回檔的 details
  const detailMapByListingId = _.reduce(proposalResults, (collect, proposalResult) => {
    // [error, payload]
    const [, payload] = proposalResult
    const proposalId = _.get(payload, 'id')
    const listingId = _.get(payload, 'listing.id')
    const batchType = _.get(payload, 'batch.type')
    const isExecute = (
      batchType === BATCH_PROPOSAL_TYPE.EXECUTE ||
      // 立即執行時不會帶 batch 所以 api return 不會有 batch 欄位，以此判斷為 executeProposalId
      _.isUndefined(batchType)
    )
    const isRevert = batchType === BATCH_PROPOSAL_TYPE.REVERT
    collect[listingId] = {
      ...(_.get(collect, listingId, { listingId })),
      ...(isExecute && { executeProposalId: proposalId }),
      ...(isRevert && { revertProposalId: proposalId })
    }
    return collect
  }, {})
  const details = _.flow(
    () => _.values(detailMapByListingId),
    // 清掉 executeProposalId 是 undefined 的資料
    // 預約/立即執行提案編號為批次的必填欄位，有 undefined 可能是提案送出沒有成功但是避免整體都被影響
    listingIdDetails => _.filter(listingIdDetails, ({ executeProposalId }) => {
      return !_.isUndefined(executeProposalId)
    })
  )()
  return { id: batchId, details }
}

export const getBatchId = (successProposalMap) => {
  const batchId = _.flow(
    () => _.values(successProposalMap),
    _.first,
    successProposal => _.get(successProposal, 'batch.id')
  )()
  return batchId
}
